import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { SECTORS, FundHouse, Holding, Mapping } from "./shared";
import { compareSectorNames } from "@/lib/sectorSort";

export default function FundHouseSectorMapping() {
  const [houses, setHouses] = useState<FundHouse[]>([]);
  const [selectedName, setSelectedName] = useState<string>("");
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [loading, setLoading] = useState(true);

  const houseNames = useMemo(() => {
    const set = new Set<string>();
    houses.forEach(h => set.add(h.name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [houses]);

  const selectedHouse = useMemo(
    () => houses.find(h => h.name === selectedName) || null,
    [houses, selectedName]
  );

  const loadHouses = async () => {
    const hRes = await supabase.from("bm_fund_houses").select("*").eq("is_active", true).order("benchmark").order("sort_order").order("name");
    if (hRes.error) toast.error(hRes.error.message);
    setHouses((hRes.data || []) as FundHouse[]);
  };

  const loadHoldings = async () => {
    if (!selectedName) { setHoldings([]); setMappings([]); setLoading(false); return; }
    setLoading(true);
    const houseIds = houses.filter(h => h.name === selectedName).map(h => h.id);
    if (!houseIds.length) { setHoldings([]); setMappings([]); setLoading(false); return; }
    const hldRes = await supabase.from("bm_fund_house_holdings").select("*").in("fund_house_id", houseIds).order("sort_order").order("holding_name");
    if (hldRes.error) toast.error(hldRes.error.message);
    const hldRows = (hldRes.data || []) as Holding[];
    setHoldings(hldRows);
    const hldIds = hldRows.map(h => h.id);
    if (hldIds.length) {
      const mRes = await supabase.from("bm_holding_sector_mappings").select("*").in("holding_id", hldIds);
      if (mRes.error) toast.error(mRes.error.message);
      setMappings((mRes.data || []) as Mapping[]);
    } else setMappings([]);
    setLoading(false);
  };

  useEffect(() => { loadHouses().finally(() => setLoading(false)); }, []);
  useEffect(() => { if (!selectedName && houseNames.length) setSelectedName(houseNames[0]); }, [houseNames, selectedName]);
  useEffect(() => { loadHoldings(); /* eslint-disable-next-line */ }, [selectedName, houses.length]);

  const mappingsByHolding = useMemo(() => {
    const m: Record<string, Mapping[]> = {};
    mappings.forEach(x => { (m[x.holding_id] ||= []).push(x); });
    return m;
  }, [mappings]);

  // Group holdings by name across all 5 benchmark variants
  const holdingsByName = useMemo(() => {
    const m = new Map<string, Holding[]>();
    holdings.forEach(h => {
      if (!m.has(h.holding_name)) m.set(h.holding_name, []);
      m.get(h.holding_name)!.push(h);
    });
    return m;
  }, [holdings]);

  const holdingNames = useMemo(
    () => Array.from(holdingsByName.keys()).sort((a, b) => a.localeCompare(b)),
    [holdingsByName]
  );

  // NOTE on performance: the DB has a per-row mirror trigger
  // (bm_mirror_holding_sector_mappings) that propagates INSERT/UPDATE/DELETE
  // to all sibling BM variants of the same fund house + holding name, and a
  // per-statement recompute trigger on bm_holding_sector_mappings. So the
  // client only needs to touch the representative row for one variant — the
  // DB does the rest. Touching every variant row from the client turned each
  // edit into N sibling UPDATE statements × N mirror upserts × per-statement
  // recomputes, which was blowing past the 8s authenticated statement timeout
  // for large fund houses (e.g. L&G Multi-Index across BM1-5).

  const addMappingGroup = async (holdingIds: string[]) => {
    // Aggregate sectors already used across ALL variants to avoid unique-constraint clashes
    const used = new Set<string>();
    holdingIds.forEach(id => (mappingsByHolding[id] || []).forEach(m => used.add(m.cbam_sector_name)));
    const next = SECTORS.find(s => !used.has(s));
    if (!next) { toast.error("All sectors already mapped"); return; }
    const repHoldingId = holdingIds[0];
    // Insert on the representative variant only; mirror trigger fans out to siblings.
    const { error } = await supabase
      .from("bm_holding_sector_mappings")
      .upsert(
        [{ holding_id: repHoldingId, cbam_sector_name: next, weight_pct: 0 }],
        { onConflict: "holding_id,cbam_sector_name", ignoreDuplicates: true },
      );
    if (error) return toast.error(error.message);
    loadHoldings();
  };

  const updateMappingGroup = async (holdingIds: string[], sectorName: string, patch: Partial<Mapping>) => {
    const repHoldingId = holdingIds[0];
    const repId = mappings.find(m => m.holding_id === repHoldingId && m.cbam_sector_name === sectorName)?.id;
    if (!repId) return;

    // If renaming sector, delete any pre-existing rows on the SAME holdings using the
    // target sector so the unique (holding_id, cbam_sector_name) constraint doesn't
    // trip when the mirror trigger propagates the rename.
    if (patch.cbam_sector_name && patch.cbam_sector_name !== sectorName) {
      const newSector = patch.cbam_sector_name;
      const conflictIds = mappings
        .filter(m => holdingIds.includes(m.holding_id) && m.cbam_sector_name === newSector)
        .map(m => m.id);
      if (conflictIds.length) {
        const { error: delErr } = await supabase.from("bm_holding_sector_mappings").delete().in("id", conflictIds);
        if (delErr) return toast.error(delErr.message);
      }
    }

    const { error } = await supabase.from("bm_holding_sector_mappings").update(patch).eq("id", repId);
    if (error) return toast.error(error.message);
    loadHoldings();
  };

  const deleteMappingGroup = async (holdingIds: string[], sectorName: string) => {
    const repHoldingId = holdingIds[0];
    const repId = mappings.find(m => m.holding_id === repHoldingId && m.cbam_sector_name === sectorName)?.id;
    if (!repId) return;
    // Delete rep only; mirror trigger removes the same sector on sibling variants.
    const { error } = await supabase.from("bm_holding_sector_mappings").delete().eq("id", repId);
    if (error) return toast.error(error.message);
    loadHoldings();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle>Fund House BM Sector Mapping</CardTitle>
            <CardDescription>Select a fund house to manage its CBAM Investment Sector weights for each holding.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Fund House:</span>
            <Select value={selectedName} onValueChange={setSelectedName}>
              <SelectTrigger className="w-[280px]"><SelectValue placeholder="Select fund house" /></SelectTrigger>
              <SelectContent>
                {houseNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : !selectedHouse ? (
          <p className="text-sm text-muted-foreground italic">Select a fund house above.</p>
        ) : holdingNames.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No holdings yet. Add holdings via the Fund House BM Holdings tab.</p>
        ) : (
          <div className="space-y-3">
            {holdingNames.map(name => {
              const variants = [...(holdingsByName.get(name) || [])].sort(
                (a: any, b: any) => {
                  const ah = houses.find(h => h.id === a.fund_house_id);
                  const bh = houses.find(h => h.id === b.fund_house_id);
                  return (ah?.benchmark ?? 0) - (bh?.benchmark ?? 0);
                }
              );
              const ids = variants.map(v => v.id);
              const repId = ids[0];
              const hMaps = mappingsByHolding[repId] || [];
              const total = hMaps.reduce((s, m) => s + (Number(m.weight_pct) || 0), 0);
              // Per-BM variant gap detection: a sibling variant exists for this
              // holding but has no mapping rows, or its mapping total deviates
              // from the representative's total.
              const variantStatus = variants.map(v => {
                const h = houses.find(x => x.id === (v as any).fund_house_id);
                const vm = mappingsByHolding[v.id] || [];
                const vtotal = vm.reduce((s, m) => s + (Number(m.weight_pct) || 0), 0);
                return { bm: h?.benchmark, mapCount: vm.length, total: vtotal };
              });
              const gapBMs = variantStatus
                .filter(s => s.mapCount === 0 || Math.abs(s.total - total) > 0.05)
                .map(s => `BM${s.bm}`);
              return (
                <div key={name} className="border rounded-md p-3 space-y-3 bg-muted/20">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{name}</span>
                    <Button size="sm" variant="outline" onClick={() => addMappingGroup(ids)}>
                      <Plus className="h-4 w-4 mr-1" /> Add Sector
                    </Button>
                    {gapBMs.length > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive">
                        Mapping gap: {gapBMs.join(", ")} — click Add Sector to resync, or edit a weight to push to all variants
                      </span>
                    )}
                    <span className={`ml-auto text-xs tabular-nums ${Math.abs(total - 100) < 0.05 ? "text-muted-foreground" : "text-destructive"}`}>
                      Total: {total.toFixed(2)}%
                    </span>
                  </div>

                  {hMaps.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>CBAM Investment Sector</TableHead>
                          <TableHead className="w-[160px]">Weight (%)</TableHead>
                          <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...hMaps].sort((a, b) => compareSectorNames(a.cbam_sector_name, b.cbam_sector_name)).map(m => (
                          <TableRow key={m.id}>
                            <TableCell>
                              <Select value={m.cbam_sector_name} onValueChange={(v) => updateMappingGroup(ids, m.cbam_sector_name, { cbam_sector_name: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.01" defaultValue={Number(m.weight_pct)}
                                onBlur={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (!isNaN(v) && v !== Number(m.weight_pct)) updateMappingGroup(ids, m.cbam_sector_name, { weight_pct: v });
                                }} />
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" onClick={() => deleteMappingGroup(ids, m.cbam_sector_name)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
